import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useAppSelector } from 'common';
import { RecipeHeaderPrivateUser } from 'common/src/api/models/recipe-header-private-user.models';
import { RecipeHeader } from 'common/src/api/models/recipe-header.models';
import { selectAllRecipeHeaderPrivateUsersData } from 'common/src/store/recipes';
import { CfDataGridTableViewDefinitions } from '../../../../cf-ui/DataGrid/CfDataGridProps';
import { RecipeGridRowImage } from '../../../recipes/components/RecipeGrid/RecipeGridRowImage';
import SecretRecipeRowActionsButton from '../secretRowAction/secretRecipeRowActionButton';

export const secretRecipesTableView = (): CfDataGridTableViewDefinitions<RecipeHeader> => {
  return {
    xs: {
      showHeader: true,
      bodyTableRowProps: () => ({
        sx: {
          alignItems: 'center',
          '&:hover': { backgroundColor: '#f5f5f5' },
        },
      }),

      columns: [
        {
          title: '',
          width: '30%',
          value: function ImageURL(props) {
            return <RecipeGridRowImage imageUrl={props.item.ImageUrl} size='small' />;
          },
          loadingPlaceholder: <Skeleton width='100%' height='100px' />,
        },
        {
          title: (
            <Stack>
              <ColumnHeader header='Recipe Name' />
              <ColumnHeader header='Category' />
              <ColumnHeader header='Users' />
            </Stack>
          ),
          width: '70%',
          value: function RecipeNameCategoryAndUsers(props) {
            return (
              <Stack>
                <Stack display='flex' flexDirection='row' justifyContent='space-between'>
                  <Typography variant='h4' textAlign='left'>
                    {props.item.RecipeName}
                  </Typography>
                  <SecretRecipeRowActionsButton recipeHeader={props.item} />
                </Stack>

                <Typography variant='responsiveParagraph' sx={{ mb: 2 }}>
                  {props.item.CategoryName}
                </Typography>

                <Users recipeHeader={props.item} hideKebab={true} />
              </Stack>
            );
          },
          loadingPlaceholder: (
            <Stack>
              <Skeleton width='100%' />
              <Skeleton width='100%' />
              <Skeleton width='100%' />
            </Stack>
          ),
        },
      ],
    },
    lg: {
      showHeader: true,
      bodyTableRowProps: () => ({
        sx: {
          alignItems: 'center',
          '&:hover': { backgroundColor: '#f5f5f5' },
        },
      }),

      columns: [
        {
          title: '',
          width: '20%',
          value: function ImageURL(props) {
            return <RecipeGridRowImage imageUrl={props.item.ImageUrl} size='small' />;
          },
          loadingPlaceholder: <Skeleton width='100%' height='100px' />,
        },
        {
          title: (
            <Stack>
              <ColumnHeader header='Recipe Name' />
              <ColumnHeader header='Category' />
            </Stack>
          ),
          width: '40%',
          value: function RecipeNameAndCategory(props) {
            return (
              <Stack>
                <Typography variant='h4'>{props.item.RecipeName}</Typography>
                <Typography variant='responsiveParagraph'>{props.item.CategoryName}</Typography>
              </Stack>
            );
          },
          loadingPlaceholder: (
            <Stack>
              <Skeleton width='100%' />
              <Skeleton width='100%' />
            </Stack>
          ),
        },
        {
          title: (
            <Stack display='grid' gridTemplateColumns='85% 15%' gap={2} width='100%'>
              <Stack display='grid' gridTemplateColumns='65% 35%' gap={2} width='100%'>
                <ColumnHeader header='Users' />
              </Stack>
              <></>
            </Stack>
          ),
          width: '40%',
          value: function PrivateUsers(props) {
            return <Users recipeHeader={props.item} />;
          },
          loadingPlaceholder: <Skeleton width='100%' />,
        },
      ],
    },
    xl: {
      showHeader: true,
      bodyTableRowProps: () => ({
        sx: {
          alignItems: 'center',
          '&:hover': { backgroundColor: '#f5f5f5' },
        },
      }),

      columns: [
        {
          title: '',
          width: '15%',
          value: function ImageURL(props) {
            return <RecipeGridRowImage imageUrl={props.item.ImageUrl} size='small' />;
          },
          loadingPlaceholder: <Skeleton width='100%' height='100px' />,
        },
        {
          title: 'Recipe name',
          width: '25%',
          value: function RecipeName(props) {
            return (
              <Typography variant='responsiveParagraph' textAlign='left'>
                {props.item.RecipeName}
              </Typography>
            );
          },
          loadingPlaceholder: <Skeleton width='80%' />,
        },
        {
          title: 'Category',
          width: '25%',
          value: function CategoryName(props) {
            return <Typography variant='responsiveParagraph'>{props.item.CategoryName}</Typography>;
          },
          loadingPlaceholder: <Skeleton width='80%' />,
        },
        {
          title: 'Users',
          width: '35%',
          value: function PrivateUsers(props) {
            return <Users recipeHeader={props.item} />;
          },
          loadingPlaceholder: <Skeleton width='80%' />,
        },
      ],
    },
  };
};

const ColumnHeader = ({
  header,
  textAlign,
}: {
  header: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
}) => {
  return (
    <Typography
      variant='h6'
      sx={{
        fontSize: '12px',
        fontWeight: 'bold',
        textAlign: textAlign ?? 'left',
      }}
    >
      {header}
    </Typography>
  );
};

const Users = ({ recipeHeader, hideKebab }: { recipeHeader: RecipeHeader; hideKebab?: boolean }) => {
  const isLoading = useAppSelector((s) => s.recipes.isRecipeHeaderPrivateUsersLoading);
  const privateUsers = useAppSelector((s) => selectAllRecipeHeaderPrivateUsersData(s, recipeHeader.RecipeHeaderId));

  if (isLoading) return <Skeleton width='100%' />;
  return (
    <Stack display='grid' gridTemplateColumns='85% 15%' gap={2} width='100%'>
      <Stack flexDirection='column' gap={2}>
        {[...privateUsers]
          .sort((a, b) => a.UserDisplay.localeCompare(b.UserDisplay))
          .map((users: RecipeHeaderPrivateUser) => (
            <Stack
              key={users.RecipeHeaderPrivateUserId}
              display='grid'
              gridTemplateColumns='65% 35%'
              gap={2}
              width='100%'
            >
              <Box>
                <Typography variant='responsiveParagraph'>{users.UserDisplay}</Typography>
              </Box>
            </Stack>
          ))}
      </Stack>
      {!hideKebab && <SecretRecipeRowActionsButton recipeHeader={recipeHeader} />}
    </Stack>
  );
};
